import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';
import LoginGuard from '../common/LoginGuard';
import { validateImageFiles, FileValidationResult } from '../../utils/fileValidation';
import { useToast } from '../ui/Toast';

interface InquiryData {
  category: string;
  title: string;
  content: string;
  orderNumber?: string;
  attachments?: File[];
}

const Inquiry: React.FC = () => {
  const toast = useToast();
  const { user, isLoggedIn } = useAuth();
  const { inquiries, addInquiry, isLoading } = useAdmin();
  const [formData, setFormData] = useState<InquiryData>({ category: '', title: '', content: '', orderNumber: '' });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentView, setCurrentView] = useState<'form' | 'list'>('form');
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [helpOpen, setHelpOpen] = useState(false);

  const userInquiries = useMemo(() => {
    if (!user) return [];
    return inquiries.filter(inquiry => inquiry.userEmail === user.email);
  }, [inquiries, user]);

  const categories = [
    { value: 'payment', label: 'Order / Payment' },
    { value: 'delivery', label: 'Shipping' },
    { value: 'product', label: 'Product' },
    { value: 'return', label: 'Cancellation / Refund' },
    { value: 'general', label: 'Other' }
  ];

  const handleInputChange = (field: keyof InquiryData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validation: FileValidationResult = validateImageFiles([...attachments, ...newFiles], 3);
      if (!validation.isValid) { setFileErrors(validation.errors); e.target.value = ''; return; }
      setAttachments(validation.validFiles); setFileErrors([]);
    }
  };

  const removeAttachment = (index: number) => { setAttachments(prev => prev.filter((_, i) => i !== index)); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category || !formData.title || !formData.content) { toast.warning('Please fill in all required fields.'); return; }
    setIsSubmitting(true);
    try {
      addInquiry({
        userId: user?.id || 'guest', userName: user?.name || 'Customer', userEmail: user?.email || 'anonymous@example.com',
        category: formData.category as 'product' | 'delivery' | 'payment' | 'return' | 'general',
        title: formData.title, content: formData.content, orderNumber: formData.orderNumber || undefined, status: 'pending' as const
      });
      setSubmitted(true); setIsSubmitting(false);
      setFormData({ category: '', title: '', content: '', orderNumber: '' }); setAttachments([]);
    } catch (error) { toast.error('An error occurred while submitting your inquiry. Please try again.'); setIsSubmitting(false); }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          {/* Animated checkmark */}
          <div className="relative mx-auto mb-8 w-20 h-20">
            <div className="absolute inset-0 bg-forest-100 rounded-full animate-ping opacity-20" />
            <div className="relative w-20 h-20 bg-cream-100 rounded-full flex items-center justify-center border border-forest-200">
              <svg className="w-10 h-10 text-forest-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="font-display text-display-sm text-forest-900 mb-3 italic">Message received.</h2>
          <p className="text-body text-warm-600 mb-2 leading-relaxed">We've got it. A human will reply to your email.</p>
          <p className="text-caption text-warm-500 mb-10">Typical response: 1&ndash;2 business days.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => setSubmitted(false)}
              className="btn-primary">
              Send Another
            </button>
            <Link to="/" className="btn-secondary text-center">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LoginGuard message="Please log in to submit an inquiry.">
      <div className="min-h-screen bg-cream-50">
        {/* Page Header */}
        <div className="bg-cream-100 border-b border-warm-200">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-forest-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div>
                <h1 className="font-display text-display-sm text-forest-900 italic tracking-tight">Talk to us.</h1>
                <p className="text-warm-600 text-body-sm mt-1 leading-relaxed">Orders, shipping, product questions, or anything else. Write, we'll read it.</p>
              </div>
            </div>

            {/* Pill Tabs */}
            {isLoggedIn && userInquiries.length > 0 && (
              <div className="flex bg-cream-50 rounded-xl p-1 mt-6 border border-warm-200">
                {[
                  { key: 'form', label: 'New Inquiry', icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )},
                  { key: 'list', label: `My History (${userInquiries.length})`, icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )},
                ].map(tab => (
                  <button key={tab.key} onClick={() => setCurrentView(tab.key as 'form' | 'list')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                      currentView === tab.key ? 'bg-white text-forest-900 shadow-subtle' : 'text-warm-500 hover:text-forest-700'
                    }`}>
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* How It Works — inline horizontal strip */}
        <div className="bg-cream-100 border-b border-warm-200">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between sm:justify-center sm:gap-12">
              {[
                { step: '1', text: 'Submit', icon: (
                  <svg className="w-4 h-4 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )},
                { step: '2', text: 'Review', icon: (
                  <svg className="w-4 h-4 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                )},
                { step: '3', text: 'Reply', icon: (
                  <svg className="w-4 h-4 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                )},
              ].map((item, i) => (
                <React.Fragment key={item.step}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-forest-50 rounded-lg flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-caption font-semibold text-forest-800">{item.text}</p>
                    </div>
                  </div>
                  {i < 2 && (
                    <div className="hidden sm:block w-8 h-px bg-warm-300" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* History View */}
          {currentView === 'list' && isLoggedIn && (
            <div className="space-y-4">
              {userInquiries.length > 0 ? userInquiries.map((inquiry) => (
                <div key={inquiry.id} className="bg-white rounded-2xl border border-warm-200 shadow-card overflow-hidden">
                  <div className="flex">
                    {/* Left color bar */}
                    <div className={`w-1 flex-shrink-0 ${
                      inquiry.status === 'pending' ? 'bg-gold-400' :
                      inquiry.status === 'answered' ? 'bg-forest-500' : 'bg-warm-300'
                    }`} />
                    <div className="flex-1 p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                          inquiry.status === 'pending' ? 'bg-gold-50 text-gold-700' :
                          inquiry.status === 'answered' ? 'bg-forest-50 text-forest-700' : 'bg-warm-100 text-warm-600'
                        }`}>
                          {inquiry.status === 'pending' ? 'Awaiting' : inquiry.status === 'answered' ? 'Answered' : 'Processing'}
                        </span>
                        <span className="text-[11px] text-warm-500">{categories.find(c => c.value === inquiry.category)?.label || inquiry.category}</span>
                        <span className="text-[11px] text-warm-500 ml-auto">{new Date(inquiry.createdAt).toLocaleDateString('en-US')}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-forest-900 mb-1">{inquiry.title}</h3>
                      <p className="text-sm text-warm-600 line-clamp-2">{inquiry.content}</p>

                      {/* Admin reply — indented block */}
                      {inquiry.adminReply && (
                        <div className="mt-4 ml-4 pl-4 border-l-2 border-forest-200 bg-forest-50/50 rounded-r-lg py-3 pr-4">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-5 h-5 bg-forest-100 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-forest-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <span className="text-xs font-semibold text-forest-700">From LiberaCure</span>
                            <span className="text-[11px] text-forest-500 ml-auto">{inquiry.answeredAt && new Date(inquiry.answeredAt).toLocaleDateString('en-US')}</span>
                          </div>
                          <p className="text-sm text-forest-800 whitespace-pre-wrap leading-relaxed">{inquiry.adminReply}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-16">
                  <div className="w-14 h-14 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-warm-200">
                    <svg className="w-6 h-6 text-warm-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-warm-500 text-sm">No inquiries yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Form View */}
          {currentView === 'form' && (
            <form onSubmit={handleSubmit}>
              <div className="bg-white rounded-2xl border border-warm-200 shadow-card overflow-hidden">
                <div className="p-6 sm:p-8 space-y-5">
                  {/* Category + Order Number row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-forest-700 mb-1.5">Category <span className="text-rust-600">*</span></label>
                      <select value={formData.category} onChange={(e) => handleInputChange('category', e.target.value)} required
                        className="w-full px-4 py-3 bg-cream-50 border border-warm-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 focus:bg-white outline-none transition-all">
                        <option value="">Select a category</option>
                        {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-forest-700 mb-1.5">
                        Order ID <span className="text-warm-400 font-normal">(Optional)</span>
                      </label>
                      <input type="text" value={formData.orderNumber} onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                        className="w-full px-4 py-3 bg-cream-50 border border-warm-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 focus:bg-white outline-none transition-all"
                        aria-describedby="order-id-hint" />
                      <p id="order-id-hint" className="mt-1.5 text-[11px] text-warm-500 leading-snug">
                        If this is about a specific order, paste the ID from your confirmation email.
                      </p>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-1.5">Subject <span className="text-rust-600">*</span></label>
                    <input type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-4 py-3 bg-cream-50 border border-warm-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 focus:bg-white outline-none transition-all" placeholder="A short summary — one sentence is fine" maxLength={200} required />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-1.5">Message <span className="text-rust-600">*</span></label>
                    <textarea rows={8} value={formData.content} onChange={(e) => handleInputChange('content', e.target.value)}
                      className="w-full px-4 py-3 bg-cream-50 border border-warm-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 focus:bg-white outline-none resize-none transition-all"
                      placeholder="Tell us what's going on. The more detail, the better we can help." maxLength={5000} required />
                  </div>

                  {/* File Upload — Dropzone */}
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-1.5">Attachments <span className="text-warm-400 font-normal">(Max 3 images, 5MB each)</span></label>
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-warm-300 rounded-xl cursor-pointer bg-cream-50 hover:bg-forest-50/50 hover:border-forest-400 transition-all group">
                      <div className="flex flex-col items-center">
                        <svg className="w-8 h-8 text-warm-400 group-hover:text-forest-600 transition-colors mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs text-warm-500 group-hover:text-forest-600 transition-colors">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-[11px] text-warm-400 mt-0.5">JPG, PNG, GIF, WebP up to 5MB</p>
                      </div>
                      <input type="file" onChange={handleFileChange} multiple accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp" className="hidden" />
                    </label>
                    {fileErrors.length > 0 && (
                      <div className="mt-2 p-3 bg-rust-50 border border-rust-200 rounded-xl">
                        {fileErrors.map((err, i) => <p key={i} className="text-xs text-rust-600">{err}</p>)}
                      </div>
                    )}
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-forest-50 px-4 py-2.5 rounded-xl">
                            <div className="flex items-center gap-2 min-w-0">
                              <svg className="w-4 h-4 text-forest-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="text-xs text-forest-800 truncate">{file.name}</span>
                              <span className="text-[11px] text-warm-500 flex-shrink-0">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                            </div>
                            <button type="button" onClick={() => removeAttachment(index)} className="text-warm-400 hover:text-rust-600 transition-colors ml-2 flex-shrink-0">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Privacy + Submit — bottom bar */}
                <div className="border-t border-warm-200 p-6 sm:p-8 bg-cream-50">
                  <label className="flex items-start gap-3 cursor-pointer mb-5">
                    <input type="checkbox" required className="w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20 mt-0.5" />
                    <div className="text-sm text-warm-700">
                      <span className="font-medium text-forest-800">Consent to handle my message <span className="text-rust-600">*</span></span>
                      <p className="text-xs text-warm-500 mt-0.5 leading-relaxed">We use your name and email only to reply. Nothing stored after resolution, nothing shared.</p>
                    </div>
                  </label>
                  <button type="submit" disabled={isSubmitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? <><div className="w-4 h-4 border-2 border-cream-100 border-t-transparent rounded-full animate-spin" />Sending…</> : <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                      Send Inquiry
                    </>}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Need Help? — Collapsible section */}
          <div className="mt-8">
            <button
              onClick={() => setHelpOpen(!helpOpen)}
              className="w-full flex items-center justify-between bg-white rounded-2xl border border-warm-200 shadow-card px-6 py-4 text-left hover:bg-cream-50/70 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-semibold text-forest-900">Need help faster?</span>
              </div>
              <svg className={`w-4 h-4 text-warm-500 transition-transform ${helpOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {helpOpen && (
              <div className="bg-white rounded-b-2xl border border-t-0 border-warm-200 shadow-card px-6 py-5 -mt-1 space-y-5">
                {/* Response Time */}
                <div>
                  <h4 className="text-[11px] font-semibold text-warm-500 uppercase tracking-[0.15em] mb-3">Response Time</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-warm-600">Typical reply</span>
                      <span className="font-medium text-forest-800">1&ndash;2 business days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-600">Days we're active</span>
                      <span className="text-warm-700">Monday&ndash;Friday</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-600">Weekends</span>
                      <span className="text-warm-500">Messages read, replies go out Monday</span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-warm-200" />
                {/* Read this first */}
                <div>
                  <h4 className="text-[11px] font-semibold text-warm-500 uppercase tracking-[0.15em] mb-3">Read this first</h4>
                  <div className="space-y-1.5">
                    <Link to="/honestly" className="flex items-center gap-2 text-sm text-warm-700 hover:text-gold-700 transition-colors py-1.5 group">
                      <svg className="w-3.5 h-3.5 text-warm-400 group-hover:text-gold-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Is LiberaCure legit? (Read us honestly)
                    </Link>
                    <Link to="/policy/shipping-dispatch-policy" className="flex items-center gap-2 text-sm text-warm-700 hover:text-gold-700 transition-colors py-1.5 group">
                      <svg className="w-3.5 h-3.5 text-warm-400 group-hover:text-gold-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      How long does shipping take?
                    </Link>
                    <Link to="/payment-guide" className="flex items-center gap-2 text-sm text-warm-700 hover:text-gold-700 transition-colors py-1.5 group">
                      <svg className="w-3.5 h-3.5 text-warm-400 group-hover:text-gold-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      How does crypto checkout work?
                    </Link>
                    <Link to="/policy/cancellation-and-refund-policy" className="flex items-center gap-2 text-sm text-warm-700 hover:text-gold-700 transition-colors py-1.5 group">
                      <svg className="w-3.5 h-3.5 text-warm-400 group-hover:text-gold-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Refunds, reships, and what happens if it doesn't arrive
                    </Link>
                    <Link to="/policy/restricted-substances" className="flex items-center gap-2 text-sm text-warm-700 hover:text-gold-700 transition-colors py-1.5 group">
                      <svg className="w-3.5 h-3.5 text-warm-400 group-hover:text-gold-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      What we don't ship (and why)
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LoginGuard>
  );
};

export default Inquiry;
